import {useMutation} from '@tanstack/react-query';
import {apiClient} from '@common/http/query-client';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {toast} from '@ui/toast/toast';
import {message} from '@ui/i18n/message';

/**
 * Which of the two products this browser is looking at.
 *
 * There is one student portal. A student opens it and sees their own file; a
 * member of staff opens a student from the workspace and sees the same screens
 * filled with that student's file. Nothing here decides anything — the server
 * already did, in StudentViewContext, and enforces it again on every request.
 * This is the same answer, delivered early enough that the router can send a
 * staff account to the workspace without a student dashboard flashing up first.
 */

export interface StudentViewSubject {
  id: number;
  name: string;
  email: string;
  image: string | null;
  crm_client_id: number | null;
}

export interface StudentViewState {
  /** An internal account — somebody who works here. */
  is_staff: boolean;
  /** May open a student's portal. Narrower than `is_staff`. */
  can_open_portals: boolean;
  /** The student currently open, or null. */
  student: StudentViewSubject | null;
  /**
   * Set when this session is signed in AS somebody, via "Log in as this user".
   * Different from `student`: there, `auth()` is still the staff member and the
   * portal is read-only; here `auth()` really is the other person.
   */
  impersonating: {name: string} | null;
}

const EMPTY: StudentViewState = {
  is_staff: false,
  can_open_portals: false,
  student: null,
  impersonating: null,
};

/**
 * Read from bootstrap rather than fetched.
 *
 * It has to be available synchronously inside a route loader, and it changes
 * only when the view is opened or closed — both of which reload the page on
 * purpose (see below).
 */
export function studentView(): StudentViewState {
  return ((getBootstrapData() as any)?.student_view as StudentViewState) ?? EMPTY;
}

export function useStudentView(): StudentViewState {
  return studentView();
}

/** True only while staff are looking at somebody else's portal. */
export function isViewingStudent(): boolean {
  return studentView().student !== null;
}

/**
 * Staff who have no student open. The people who must not be sent to `/student`.
 *
 * Not "is an admin": an admin who has opened a student is looking at a real
 * portal and belongs there.
 */
export function isStaffWithoutStudent(): boolean {
  const view = studentView();

  return view.is_staff && view.student === null;
}

/**
 * Open a student's portal, then hard-navigate into it.
 *
 * A full page load, not a client-side route change, and deliberately so: every
 * cached query in this tab belongs to the previous subject. Dropping the whole
 * React Query cache by reloading is the only way to be certain that no panel
 * keeps rendering the last student's invoices for a beat after the new one
 * opens — and a beat is long enough to read.
 */
export function useOpenStudentPortal(destination = '/student') {
  return useMutation({
    mutationFn: (userId: number) =>
      apiClient.post<StudentViewState>(`student/portal/view-as/${userId}`).then(r => r.data),
    onSuccess: () => {
      window.location.href = safeStudentPath(destination);
    },
    onError: () => {
      toast.danger(message("Couldn't open that student's portal."));
    },
  });
}

/**
 * Stop being somebody else.
 *
 * Lands on the staff workspace rather than reloading the student page as
 * yourself: that page would be empty and the person just said they were done
 * with it. A full navigation for the same reason as opening — the cache in this
 * tab still belongs to the student.
 */
export function useCloseStudentPortal(destination = '/dashboard/students') {
  return useMutation({
    mutationFn: () => apiClient.delete<StudentViewState>('student/portal/view-as').then(r => r.data),
    onSuccess: () => {
      window.location.href = destination;
    },
  });
}

/**
 * A destination inside the student portal, or the portal's front page.
 *
 * The path arrives in a query string, so it is attacker-controllable in the
 * usual sense: a link could carry `next=https://elsewhere/`. Only same-origin
 * paths under `/student` are honoured, which is the only thing this is for.
 */
export function safeStudentPath(path: string | null | undefined): string {
  if (!path) return '/student';

  // `//evil.com` is a protocol-relative URL, not a path.
  const looksInternal = path.startsWith('/student') && !path.startsWith('//');

  return looksInternal ? path : '/student';
}

/**
 * Recover a tab whose idea of the student view has gone stale.
 *
 * `studentView()` is read from bootstrap, which is fixed at page load. A tab
 * left open on `/student/...` while the view was closed somewhere else — a
 * second tab, the back button restoring a cached page — keeps believing a
 * student is open and asks the portal for a file it is no longer allowed to
 * read. The server answers 409 and names where the caller belongs.
 *
 * So: go there, once. The alternative is what the console showed — every panel
 * on the page failing separately, three times each, with nothing telling the
 * person why.
 */
let recovering = false;

export function installStudentViewRecovery(): void {
  apiClient.interceptors.response.use(
    response => response,
    error => {
      const workspace = error?.response?.data?.staff_workspace;

      // The account was deactivated while this tab was open: the server has
      // already ended the session (ForbidBannedUser), so go to sign-in with the
      // reason instead of failing every panel.
      if (
        error?.response?.data?.code === 'account_inactive' &&
        !window.location.pathname.startsWith('/login')
      ) {
        window.location.href = '/login?account=inactive';
        return Promise.reject(error);
      }

      if (
        !recovering &&
        error?.response?.status === 409 &&
        typeof workspace === 'string' &&
        !window.location.pathname.startsWith(workspace)
      ) {
        recovering = true;
        // Same as the route guard: remember the page they were on, so choosing
        // a student takes them back to it rather than to the portal's front
        // page they never asked for.
        const next = encodeURIComponent(
          `${window.location.pathname}${window.location.search}`,
        );
        window.location.href = `${workspace}?next=${next}`;
      }

      return Promise.reject(error);
    },
  );
}
